import { useCallback, useEffect, useState } from "react";
import { Markdown } from "../../components/Markdown";
import {
  api,
  type AdminFeedbackRow,
  type AuthorKind,
  type FeedbackStatus,
  type FeedbackType,
} from "../../lib/api";

const STATUSES: FeedbackStatus[] = [
  "new",
  "acknowledged",
  "todo",
  "done",
  "archived",
];

const TYPES: FeedbackType[] = ["feature_request", "feedback"];
const KINDS: AuthorKind[] = ["user", "mcp"];

const STATUS_CHIP: Record<FeedbackStatus, string> = {
  new: "bg-slate-100 text-slate-700",
  acknowledged: "bg-blue-100 text-blue-800",
  todo: "bg-amber-100 text-amber-800",
  done: "bg-emerald-100 text-emerald-800",
  archived: "bg-slate-100 text-slate-500",
};

const TYPE_CHIP: Record<FeedbackType, string> = {
  feedback: "bg-rose-100 text-rose-800",
  feature_request: "bg-indigo-100 text-indigo-800",
};

const KIND_CHIP: Record<AuthorKind, string> = {
  user: "bg-slate-100 text-slate-700",
  mcp: "bg-indigo-100 text-indigo-700",
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

/** Admin → Feedback. Filter, change status, hard-delete spam. */
export function FeedbackPage() {
  const [rows, setRows] = useState<AdminFeedbackRow[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [kindFilter, setKindFilter] = useState<string>("");
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.admin.feedback
      .list({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        author_kind: kindFilter || undefined,
        q: q || undefined,
      })
      .then((res) => setRows(res.items))
      .catch((e: Error) => setError(e.message));
  }, [statusFilter, typeFilter, kindFilter, q]);

  useEffect(load, [load]);

  async function changeStatus(id: string, newStatus: FeedbackStatus) {
    setBusy(`status:${id}`);
    setError(null);
    try {
      await api.admin.feedback.patchStatus(id, newStatus);
      // Refetch with the active filters applied — patching in place would
      // leave a row visible that no longer matches the selected status
      // filter (e.g. filter=new but the row is now done), making the table
      // contradict the filter chrome.
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!confirm("Hard-delete this feedback message? Prefer status=archived for audit.")) {
      return;
    }
    setBusy(`del:${id}`);
    setError(null);
    try {
      await api.admin.feedback.remove(id);
      setRows((cur) => (cur ? cur.filter((r) => r.id !== id) : cur));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Feedback</h1>
      <p className="mb-4 text-sm text-slate-500">
        Submissions from the in-app Feedback button and the MCP{" "}
        <code>submit_feedback</code> tool. Move to <code>archived</code> when
        triage is done; only hard-delete spam.
      </p>

      <section className="mb-4 grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600">Origin</label>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600">Search</label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="title or body"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </div>
      </section>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {rows === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          No matching feedback.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_CHIP[r.type]}`}>
                      {r.type}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CHIP[r.status]}`}>
                      {r.status}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${KIND_CHIP[r.author_kind]}`}>
                      {r.author_kind}
                    </span>
                    {r.type === "feature_request" && (
                      <span className="text-xs text-slate-500">
                        {r.vote_count} vote{r.vote_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <h2 className="text-base font-semibold">{r.title}</h2>
                  <Markdown
                    source={r.body}
                    className="mt-1 text-slate-700"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    {r.author_email} · {formatTime(r.created_at)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  <select
                    value={r.status}
                    disabled={busy === `status:${r.id}`}
                    onChange={(e) =>
                      changeStatus(r.id, e.target.value as FeedbackStatus)
                    }
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    disabled={busy === `del:${r.id}`}
                    className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
